import {
  CITY_LOOKUP,
  INTEREST_LOOKUP,
  MAX_NGRAM,
  OCCASION_LOOKUP,
  RELATIONSHIP_LOOKUP,
  SUGGESTION_POOLS,
  normalizeKey,
} from './signalDictionary'

// TEMPORARY (concept-proving): two jobs the real parser will own server-side
// (later an LLM), split here because they belong on opposite halves of a signal.
//
// conformSignalText fixes a *misspelling* in what the buyer typed — "chrismas" →
// "Christmas", "Seatle" → "Seattle" — and nothing else. It never swaps their
// phrasing for ours: "our anniversary" is not a mistake, so it stays exactly as
// written. That text is what they see, so rewriting it into our vocabulary reads
// like the field arguing with them.
//
// canonicalSignalValue does the opposite, out of sight: it reads their phrasing
// down to the term the endpoint matches on, pulling "anniversary" out of "our
// anniversary".
//
// The load-bearing rule for both: never guess. Without a confident match the text
// passes through untouched, which is what lets these blanks carry the long-tail
// values the curated lists will never hold ("roommate", "pottery").

type ConformableSignal = 'relationship' | 'location' | 'interests' | 'occasion'

// Below four characters a single edit is most of the word, so "cat" would happily
// become "car". Typos only get corrected once there's enough word to be sure.
const MIN_FUZZY_LENGTH = 4

// Occasions are stored lowercase, which reads correctly in the sentence ("it's for
// birthday"). Only the ones that are actually proper nouns get capitals.
const PROPER_NOUN_OCCASIONS: Record<string, string> = {
  christmas: 'Christmas',
  hanukkah: 'Hanukkah',
  "valentine's day": "Valentine's Day",
  "mother's day": "Mother's Day",
  "father's day": "Father's Day",
  'new year': 'New Year',
}

function levenshtein (a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i)

  for (let i = 1; i <= a.length; i++) {
    const current = [i]
    for (let j = 1; j <= b.length; j++) {
      const substitution = previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, substitution)
    }
    previous = current
  }

  return previous[b.length]
}

// Longer words tolerate more slop: one edit at five characters, two at eight.
function fuzzyBudget (phrase: string): number {
  return Math.floor(phrase.length / 4)
}

function closestInPool (pool: string[], phrase: string): string | null {
  if (phrase.length < MIN_FUZZY_LENGTH) return null

  const budget = fuzzyBudget(phrase)
  if (budget < 1) return null

  let best: string | null = null
  let bestDistance = budget + 1

  for (const candidate of pool) {
    const normalized = normalizeKey(candidate)
    if (Math.abs(normalized.length - phrase.length) > budget) continue

    const distance = levenshtein(normalized, phrase)
    if (distance < bestDistance) {
      best = normalized
      bestDistance = distance
    }
  }

  return best
}

function ngrams (tokens: string[]): string[] {
  const phrases: string[] = []
  for (let size = Math.min(MAX_NGRAM, tokens.length); size >= 1; size--) {
    for (let i = 0; i + size <= tokens.length; i++)
      phrases.push(tokens.slice(i, i + size).join(' '))
  }

  return phrases
}

// Exact hit on the whole phrase, then on any n-gram inside it (so "their birthday"
// finds "birthday"), then a fuzzy pass for typos. Longest n-grams are tried first
// so "mother's day" wins over "mother".
function matchAgainst (
  lookup: Map<string, string>,
  pool: string[],
  typed: string,
): string | null {
  const whole = normalizeKey(typed)
  const exact = lookup.get(whole)
  if (exact) return exact

  const tokens = whole.split(' ').filter(Boolean)
  for (const phrase of ngrams(tokens)) {
    const hit = lookup.get(phrase)
    if (hit) return hit
  }

  const fuzzy = closestInPool(pool, whole) ?? tokens.reduce<string | null>(
    (found, token) => found ?? closestInPool(pool, token),
    null,
  )

  return fuzzy ? lookup.get(fuzzy) ?? null : null
}

// The spelling a buyer was reaching for, properly cased. Occasions that are proper
// nouns get their capitals back; cities come out of the list already cased; the
// rest keep the lowercase surface word ("cowoker" → "coworker", never "colleague",
// which would be putting words in their mouth).
function correctedSpelling (name: ConformableSignal, word: string): string | null {
  const pool = SUGGESTION_POOLS[name]
  const match = closestInPool(pool, word)
  if (!match) return null

  const surface = pool.find(candidate => normalizeKey(candidate) === match)
  if (!surface) return null
  if (name !== 'occasion') return surface

  const canonical = OCCASION_LOOKUP.get(match)
  return (canonical && PROPER_NOUN_OCCASIONS[canonical]) ?? surface
}

// Nothing to fix if any part of what they wrote is already a word we know — they
// phrased it their way, which is allowed.
function containsKnownPhrase (name: ConformableSignal, typed: string): boolean {
  const pool = new Set(SUGGESTION_POOLS[name].map(normalizeKey))
  const tokens = normalizeKey(typed).split(' ').filter(Boolean)

  return ngrams(tokens).some(phrase => pool.has(phrase))
}

// Fixes a misspelling and nothing else. Returns null when there's nothing to fix,
// which includes every case where the buyer simply phrased it their own way.
export function conformSignalText (name: ConformableSignal, typed: string): string | null {
  const trimmed = typed.trim()
  if (!trimmed || containsKnownPhrase(name, trimmed)) return null

  const whole = correctedSpelling(name, normalizeKey(trimmed))
  if (whole) return whole

  // Otherwise fix the misspelled word in place and leave the rest of their
  // sentence alone: "our chrismas" becomes "our Christmas", not "Christmas".
  const words = trimmed.split(/\s+/)
  const corrected = words.map(word => correctedSpelling(name, normalizeKey(word)) ?? word)

  return corrected.some((word, i) => word !== words[i]) ? corrected.join(' ') : null
}

const CANONICAL_LOOKUPS: Record<ConformableSignal, Map<string, string>> = {
  relationship: RELATIONSHIP_LOOKUP,
  location: CITY_LOOKUP,
  interests: INTEREST_LOOKUP,
  occasion: OCCASION_LOOKUP,
}

// Occasion and relationship feed the engine's EXCLUSIVE filters — a gift tagged
// for a different occasion/recipient is dropped from every pass — so an unmatched
// value there doesn't merely miss, it excludes every gift tagged with a real one
// and degrades the whole feed to untagged generics. Unmatched text in these blanks
// must send NOTHING (the free-text parser already works this way: text it can't
// match never becomes a signal). Location and interests stay open: an unresolved
// city is re-resolved (and dropped if unresolvable) by the results surface, and an
// interest is a fit-only signal where an unknown value is a harmless no-op — which
// is what lets those blanks carry long-tail values ("pottery").
const OPEN_VOCABULARY: Set<ConformableSignal> = new Set(['location', 'interests'])

// What the endpoint receives for text the buyer sees, so the request carries one
// vocabulary whichever UI produced it. It reads their phrasing down to the term:
// "husband" arrives as "spouse", "our anniversary" as "anniversary". Returns null
// for unmatched closed-vocabulary text: the buyer's words stay on screen as the
// signal's display, but no value exists for the request to carry.
//
// This is the piece that moves server-side once the endpoint owns matching.
export function canonicalSignalValue (name: ConformableSignal, shown: string): string | null {
  const canonical = matchAgainst(CANONICAL_LOOKUPS[name], SUGGESTION_POOLS[name], shown)
  if (!canonical) return OPEN_VOCABULARY.has(name) ? shown : null

  return canonical
}

// Whether the matcher already knows this text as a term — an exact hit or a known
// phrase inside it, but not a fuzzy/typo guess (passing no pool disables the fuzzy
// pass). The madlibs input keys its write timing off this: a term we recognise can
// search at once, while one we don't waits a beat in case the buyer is mid-word.
export function recognizesSignalText (name: ConformableSignal, text: string): boolean {
  return matchAgainst(CANONICAL_LOOKUPS[name], [], text) !== null
}
